import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, unwrap } from '../../api/client';
import { errorMessage } from '../../api/errors';
import { useAuth } from '../../auth/AuthProvider';
import { Button, Field, Input, Modal, Select } from '../../design';
import { useToast } from '../../design/Toast';
import { uploadDocument, type UploadStep } from '../../lib/upload';
import { useLockerOptions } from '../operations/common';
import { useOrgOptions } from '../users/UsersPage';
import { CLASSIFICATION, type DocumentClassification, fmtBytes, useDocumentLimits } from './common';

const STEP_LABEL: Record<UploadStep, string> = {
  metadata: 'Se înregistrează metadatele…',
  upload: 'Se încarcă fișierul în stocare…',
  verify: 'Se verifică dimensiunea, tipul și hash-ul…',
};

/**
 * Încărcare (D8): fișier + clasificare + asociere (locker sau operațiune) + retenție opțională.
 * Când e deschis din detaliul unei operațiuni, operațiunea este fixă și lockerul se moștenește.
 */
export function UploadDialog({
  fixedOperation,
  onClose,
  onUploaded,
}: {
  fixedOperation?: { id: string; title: string; orgId: string };
  onClose: () => void;
  onUploaded: () => void;
}) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const limits = useDocumentLimits();
  const orgs = useOrgOptions();
  const [orgId, setOrgId] = useState(fixedOperation?.orgId ?? user?.orgId ?? '');
  const lockers = useLockerOptions(orgId || undefined);
  const [file, setFile] = useState<File | null>(null);
  const [classification, setClassification] = useState<DocumentClassification>('internal');
  const [lockerId, setLockerId] = useState('');
  const [opQuery, setOpQuery] = useState('');
  const [operationId, setOperationId] = useState(fixedOperation?.id ?? '');
  const [retentionDays, setRetentionDays] = useState('');
  const [step, setStep] = useState<UploadStep | null>(null);
  const [error, setError] = useState<string | null>(null);

  const operations = useQuery({
    queryKey: ['operations', 'search', orgId, opQuery],
    queryFn: () =>
      unwrap(
        api.GET('/operations', {
          params: { query: { q: opQuery, pageSize: 10, ...(orgId ? { orgId } : {}) } },
        }),
      ),
    enabled: !fixedOperation && opQuery.trim().length >= 2,
  });

  const accept = (limits.data?.allowedTypes ?? []).flatMap((t) => t.extensions.map((e) => `.${e}`));
  const maxBytes = (limits.data?.maxMb ?? 10) * 1024 * 1024;
  const allowedMimes = new Set<string>((limits.data?.allowedTypes ?? []).map((t) => t.mime));
  const fileProblem = !file
    ? null
    : !allowedMimes.has(file.type)
      ? 'Tip de fișier nepermis (PDF, DOCX, PNG, JPG, TXT).'
      : file.size > maxBytes
        ? `Fișierul depășește ${limits.data?.maxMb ?? 10} MB.`
        : file.size === 0
          ? 'Fișierul este gol.'
          : null;
  const needsOrg = user?.orgId === null && !fixedOperation && !orgId;

  const submit = async () => {
    if (!file || fileProblem) return;
    setError(null);
    try {
      await uploadDocument(
        file,
        {
          ...(orgId && !fixedOperation ? { orgId } : {}),
          ...(operationId ? { operationId } : lockerId ? { lockerId } : {}),
          classification,
          ...(retentionDays ? { retentionDays: Number(retentionDays) } : {}),
        },
        setStep,
      );
      toast(`Documentul „${file.name}” este disponibil.`, 'success');
      void qc.invalidateQueries({ queryKey: ['documents'] });
      onUploaded();
    } catch (e) {
      setStep(null);
      setError(errorMessage(e));
    }
  };

  return (
    <Modal
      title={fixedOperation ? `Document pentru „${fixedOperation.title}”` : 'Încarcă document'}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={step !== null}>
            Renunță
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            busy={step !== null}
            disabled={!file || !!fileProblem || needsOrg}
          >
            Încarcă
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field
          label="Fișier"
          hint={`PDF, DOCX, PNG, JPG sau TXT, maximum ${limits.data?.maxMb ?? 10} MB`}
          error={fileProblem ?? undefined}
          htmlFor="doc-file"
        >
          <Input
            id="doc-file"
            type="file"
            accept={accept.join(',')}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          {file && !fileProblem && (
            <div className="small muted" style={{ marginTop: 4 }}>
              {file.name} · {fmtBytes(file.size)}
            </div>
          )}
        </Field>
        {user?.orgId === null && !fixedOperation && (
          <Field label="Organizație" htmlFor="doc-org">
            <Select
              id="doc-org"
              value={orgId}
              onChange={(e) => {
                setOrgId(e.target.value);
                setLockerId('');
                setOperationId('');
              }}
            >
              <option value="">Alege…</option>
              {orgs.data?.items.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Clasificare" htmlFor="doc-class">
          <Select
            id="doc-class"
            value={classification}
            onChange={(e) => setClassification(e.target.value as DocumentClassification)}
          >
            {(Object.keys(CLASSIFICATION) as DocumentClassification[]).map((c) => (
              <option key={c} value={c}>
                {CLASSIFICATION[c].label}
              </option>
            ))}
          </Select>
        </Field>
        {!fixedOperation && (
          <>
            <Field
              label="Operațiune (opțional)"
              hint="Caută după titlu sau referință; lockerul se moștenește"
              htmlFor="doc-op"
            >
              <Input
                id="doc-op"
                placeholder="ex. 48213"
                value={opQuery}
                onChange={(e) => {
                  setOpQuery(e.target.value);
                  setOperationId('');
                }}
              />
              {operations.data && operations.data.items.length > 0 && !operationId && (
                <Select
                  aria-label="Rezultate operațiuni"
                  value={operationId}
                  onChange={(e) => {
                    setOperationId(e.target.value);
                    setLockerId('');
                  }}
                  style={{ marginTop: 6 }}
                >
                  <option value="">Alege operațiunea…</option>
                  {operations.data.items.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.title} · {o.reference}
                    </option>
                  ))}
                </Select>
              )}
              {operationId && (
                <div className="small flex" style={{ marginTop: 4 }}>
                  <span>Operațiune selectată.</span>
                  <Button small variant="ghost" onClick={() => setOperationId('')}>
                    Schimbă
                  </Button>
                </div>
              )}
            </Field>
            {!operationId && (
              <Field label="Locker (opțional)" htmlFor="doc-locker">
                <Select
                  id="doc-locker"
                  value={lockerId}
                  onChange={(e) => setLockerId(e.target.value)}
                >
                  <option value="">Fără locker</option>
                  {lockers.data?.items.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
          </>
        )}
        <Field
          label="Retenție (zile)"
          hint="Gol = retenția implicită a organizației; 1–3650"
          htmlFor="doc-retention"
        >
          <Input
            id="doc-retention"
            type="number"
            min={1}
            max={3650}
            value={retentionDays}
            onChange={(e) => setRetentionDays(e.target.value)}
            style={{ maxWidth: 160 }}
          />
        </Field>
        {step && (
          <p className="small muted" role="status">
            {STEP_LABEL[step]}
          </p>
        )}
        {error && (
          <p className="small" style={{ color: 'var(--red)' }} role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
